import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  forwardRef
} from "@angular/core";
import { BehaviorSubject } from "rxjs";

import { defaultThreshold } from "../../fuse/fuse-constants";
import { OptionsListComponent } from "../base";
import { TransferListOptionComponent } from "./transfer-list-option.component";
import { TransferListComponent } from "./transfer-list.component";

@Directive()
export abstract class TransferOptionsListComponent<Model>
  extends OptionsListComponent<Model>
  implements OnInit
{
  @ViewChild("header", { static: true }) header: ElementRef;
  @ViewChild("scrollContainer", { static: true }) scrollContainer: ElementRef;
  @ViewChild("query", { static: true }) queryInput: ElementRef;

  @ViewChildren(forwardRef(() => TransferListOptionComponent))
  optionComponents: QueryList<TransferListOptionComponent<Model>>;

  selected: BehaviorSubject<Model[]> = new BehaviorSubject<Model[]>([]);

  get lastSelected(): Model | undefined {
    return this._lastSelected;
  }
  set lastSelected(val: Model | undefined) {
    if (val == null) {
      this.lastSelected = undefined;
      return;
    }
    if (this.shiftDown) {
      return;
    }
    this._lastSelected = val;
  }
  private _lastSelected: Model | undefined;

  @HostListener("document:keydown", ["$event"])
  @HostListener("document:keyup", ["$event"])
  modifierKeydownHandler(event: KeyboardEvent): void {
    const keyDown: boolean = event.type === "keydown";
    if (event.key === "Shift") {
      this.shiftDown = keyDown;
      return;
    }
    if (event.key === "Meta") {
      this.cmdDown = keyDown;
      return;
    }
  }

  @Input()
  searchThreshold: number = defaultThreshold;

  @Input()
  disallowedOptions = [];

  @Input()
  disableDisallowedOptions = false;

  shiftDown: boolean = false;
  cmdDown: boolean = false;

  get noOptionsAvailable(): boolean {
    return (
      !this.parent.loadingOptions &&
      (this.options == null || this.options.length === 0)
    );
  }

  constructor(
    @Inject(forwardRef(() => TransferListComponent))
    public parent: any,
    renderer: Renderer2,
    elementRef: ElementRef
  ) {
    super(parent, renderer, elementRef);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setHostClasses();
  }

  protected setHostClasses(): void {
    this.renderer.addClass(
      this.elementRef.nativeElement,
      this.selectClasses.optionsList
    );
  }

  abstract toggleSelectedOptions(): void;

  clearSelected(): void {
    this.selected.next([]);
    delete this.lastSelected;
  }

  setSelected(model: Model): void {
    let selected: Model[];
    if (this.shiftDown && this.lastSelected == null) {
      // if no lastSelected, add all options up to & including the one clicked
      selected = this.options.slice(
        0,
        this.options.findIndex((option) => option === model) + 1
      );
      this.selected.next(selected);
      return;
    }

    if (this.shiftDown) {
      // otherwise, set selected to the range between the current clicked & lastSelected
      const sliceIndices: number[] = [
        this.options.findIndex((option) => option === this.lastSelected),
        this.options.findIndex((option) => option === model)
      ].sort((a, b) => a - b);
      selected = this.options.slice(sliceIndices[0], sliceIndices[1] + 1);
      this.selected.next(selected);
      return;
    }

    if (this.cmdDown && this.isSelected(model)) {
      // usually you cannot unselect an option, but when holding cmd, you can
      selected = this.selected.value.filter((option) => option !== model);
      this.selected.next(selected);
      return;
    }

    if (this.cmdDown) {
      // if the option isn't selected and cmdDown, add it to the array
      this.selected.next([...this.selected.value, model]);
      return;
    }

    this.selected.next([model]);
  }

  isSelected(model: Model): boolean {
    return this.selected.value.includes(model);
  }
}
